import {
    Business as Building2,
    ExpandMore as ChevronDown,
    ExpandLess as ChevronUp,
    People as Users
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import type { EntitySummary, EventSummary, NarrativeStatus, NarrativeSummary } from '@/lib/types';

import { calculateNarrativeStatus } from '@/lib/timeline-utils';


interface EventCardProps {
  event: EventSummary;
  scenes: NarrativeSummary[];
  locations: EntitySummary[];
  characters: EntitySummary[];
  organizations: EntitySummary[];
}

export function EventCard({ event, scenes, locations, characters, organizations }: EventCardProps) {

  const [expanded, setExpanded] = useState(false);

  const narrativeStatus = calculateNarrativeStatus(event, scenes);
  const relatedScenes = scenes.filter((scene) => scene.eventIds?.includes(event.id));

  const eventCharacters = characters.filter((char) => event.characterIds?.includes(char.id));
  const eventOrganizations = organizations.filter((org) => event.organizationIds?.includes(org.id));

  const getStatusColor = (status: NarrativeStatus) => {
    switch (status) {
      case 'notWritten':
        return 'default';
      case 'partial':
        return 'warning';
      case 'complete':
        return 'success';
    }
  };

  const getStatusLabel = (status: NarrativeStatus) => {
    switch (status) {
      case 'notWritten':
        return 'Not Written';
      case 'partial':
        return 'Partial';
      case 'complete':
        return 'Complete';
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        '&:hover': { boxShadow: 4 },
        transition: 'box-shadow 0.2s',
      }}
    >
      <CardContent>
        {/* Collapsed View */}
        <Box onClick={() => setExpanded(!expanded)}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 0.5 }}>
                {event.name}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {event.date && (
                  <Typography variant="body2" color="text.secondary">
                    {event.date}
                  </Typography>
                )}
                {eventCharacters.length > 0 && (
                  <Chip
                    icon={<Users sx={{ fontSize: 14 }} />}
                    label={`${eventCharacters.length}`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {eventOrganizations.length > 0 && (
                  <Chip
                    icon={<Building2 sx={{ fontSize: 14 }} />}
                    label={`${eventOrganizations.length}`}
                    size="small"
                    variant="outlined"
                  />
                )}
                <Chip
                  label={getStatusLabel(narrativeStatus)}
                  size="small"
                  color={getStatusColor(narrativeStatus)}
                />
              </Stack>
            </Box>
            <IconButton size="small">
              {expanded ? <ChevronUp sx={{ fontSize: 20 }} /> : <ChevronDown sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Box>

        {/* Expanded View */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            {/* Short Description */}
            {event.shortDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {event.shortDescription}
              </Typography>
            )}

            {/* Characters */}
            {eventCharacters.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Characters:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {eventCharacters.map((char) => (
                    <Chip key={char.id} label={char.name} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Organizations */}
            {eventOrganizations.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Organizations:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {eventOrganizations.map((org) => (
                    <Chip key={org.id} label={org.name} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Related Scenes */}
            {relatedScenes.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Related Scenes ({relatedScenes.length}):
                </Typography>
                <Stack spacing={0.5}>
                  {relatedScenes.map((scene) => (
                    <MuiLink key={scene.id} component={Link} to={`/scenes/${scene.id}`} variant="body2" sx={{ cursor: 'pointer' }}>
                      {scene.name}
                    </MuiLink>

                  ))}
                </Stack>
              </Box>
            )}

            {/* View Full Event */}
            <MuiLink component={Link} to={`/events/${event.id}`} variant="body2" sx={{ fontWeight: 'medium' }}>
              View full event details →
            </MuiLink>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}
